// src/pages/admin/AdminSocialLogs.jsx
// git commit: "feat(AdminSocialLogs): smart stock sort, status badges, notification bell"
import { useState, useEffect, useCallback, useRef } from "react";
import {
  Plus, Pencil, Trash2, ToggleLeft, ToggleRight,
  X, Save, Search, Eye, ChevronDown, ChevronRight,
  Layers, Package, Bell, CheckCheck, Trash, RefreshCw,
  AlertTriangle, ShoppingBag, AlertCircle,
} from "lucide-react";
import adminApi from "../../api/adminApi";
import { toast } from "react-toastify";

const CATEGORIES = ["facebook", "instagram", "tiktok", "twitter", "telegram", "vpn", "other"];
const LOW_STOCK_THRESHOLD = 5;

const EMPTY_FORM = {
  title: "", category: "facebook", description: "",
  priceNgn: "", previewUrl: "", country: "", quantity: 1,
  credentials: { email: "", password: "", extra: "" },
};

// ── Stock priority sort ───────────────────────────────────────────────────────
// Out of stock → Low stock → In stock (alphabetical within each tier)
function sortGroupsByPriority(groups) {
  return [...groups].sort((a, b) => {
    const tierA = a.available === 0 ? 0 : a.available <= LOW_STOCK_THRESHOLD ? 1 : 2;
    const tierB = b.available === 0 ? 0 : b.available <= LOW_STOCK_THRESHOLD ? 1 : 2;
    if (tierA !== tierB) return tierA - tierB;
    return a.title.localeCompare(b.title);
  });
}

// ── Stock badge ───────────────────────────────────────────────────────────────
function StockBadge({ available }) {
  if (available === 0) {
    return (
      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
        <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
        Out of Stock
      </span>
    );
  }
  if (available <= LOW_STOCK_THRESHOLD) {
    return (
      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0 animate-pulse" />
        Low Stock ({available})
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-semibold bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
      <span className="w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
      {available} available
    </span>
  );
}

// ── Notification bell ─────────────────────────────────────────────────────────
function NotificationBell({ notifications, unreadCount, onMarkRead, onClear, onRefresh }) {
  const [open, setOpen] = useState(false);
  const ref             = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const iconForType = (type) => {
    if (type === "sale")         return <ShoppingBag size={13} className="text-green-500" />;
    if (type === "low_stock")    return <AlertTriangle size={13} className="text-amber-500" />;
    if (type === "out_of_stock") return <AlertCircle size={13} className="text-red-500" />;
    return null;
  };

  const bgForType = (type, isRead) => {
    if (isRead) return "bg-gray-50 dark:bg-gray-800/50";
    if (type === "sale")         return "bg-green-50 dark:bg-green-900/10";
    if (type === "low_stock")    return "bg-amber-50 dark:bg-amber-900/10";
    if (type === "out_of_stock") return "bg-red-50 dark:bg-red-900/10";
    return "bg-white dark:bg-gray-900";
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-purple-400 transition"
      >
        <Bell size={18} className="text-gray-600 dark:text-gray-300" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
          {/* Bell header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full">{unreadCount}</span>
              )}
            </p>
            <div className="flex items-center gap-1">
              <button onClick={onRefresh} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition" title="Refresh">
                <RefreshCw size={13} className="text-gray-400" />
              </button>
              {unreadCount > 0 && (
                <button onClick={() => { onMarkRead(); }} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition" title="Mark all read">
                  <CheckCheck size={13} className="text-green-500" />
                </button>
              )}
              {notifications.length > 0 && (
                <button onClick={() => { onClear(); setOpen(false); }} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition" title="Clear all">
                  <Trash size={13} className="text-red-400" />
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400 dark:text-gray-500">
                No notifications
              </div>
            ) : (
              notifications.map((n) => (
                <div
                  key={n._id}
                  className={`flex items-start gap-3 px-4 py-3 border-b border-gray-50 dark:border-gray-800 last:border-0 ${bgForType(n.type, n.isRead)}`}
                >
                  <div className="flex-shrink-0 mt-0.5">{iconForType(n.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs leading-relaxed ${n.isRead ? "text-gray-500 dark:text-gray-400" : "text-gray-800 dark:text-gray-200 font-medium"}`}>
                      {n.message}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                      {new Date(n.createdAt).toLocaleString("en-NG", {
                        day: "numeric", month: "short",
                        hour: "2-digit", minute: "2-digit",
                      })}
                    </p>
                  </div>
                  {!n.isRead && <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1" />}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function AdminSocialLogs() {
  const [pageTab,         setPageTab]         = useState("manual"); // "manual" | "sameeha"
  const [accounts,        setAccounts]        = useState([]);
  const [groups,          setGroups]          = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [viewMode,        setViewMode]        = useState("grouped");
  const [showForm,        setShowForm]        = useState(false);
  const [editingId,       setEditingId]       = useState(null);
  const [form,            setForm]            = useState(EMPTY_FORM);
  const [saving,          setSaving]          = useState(false);
  const [search,          setSearch]          = useState("");
  const [filterCategory,  setFilterCategory]  = useState("all");
  const [filterSold,      setFilterSold]      = useState("all");
  const [expandedGroups,  setExpandedGroups]  = useState({});
  const [pagination,      setPagination]      = useState({ total: 0, page: 1, pages: 1 });

  // Notifications
  const [notifications,  setNotifications]   = useState([]);
  const [unreadCount,    setUnreadCount]      = useState(0);
  const notifPollRef = useRef(null);

  // ── Fetch data ──────────────────────────────────────────────────────────────
  const fetchAll = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (filterCategory !== "all") params.append("category", filterCategory);
      if (filterSold !== "all")     params.append("isSold", filterSold);

      const [allRes, groupedRes] = await Promise.all([
        adminApi.get(`/social/accounts?${params}`),
        adminApi.get("/social/grouped"),
      ]);

      if (allRes.data.success) {
        setAccounts(allRes.data.accounts || []);
        setPagination(allRes.data.pagination || {});
      }
      if (groupedRes.data.success) {
        setGroups(sortGroupsByPriority(groupedRes.data.groups || []));
      }
    } catch {
      toast.error("Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterSold]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Fetch notifications ─────────────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await adminApi.get("/social/notifications");
      if (res.data.success) {
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unreadCount || 0);
      }
    } catch { /* silent */ }
  }, []);

  // Poll notifications every 30 seconds
  useEffect(() => {
    fetchNotifications();
    notifPollRef.current = setInterval(fetchNotifications, 30000);
    return () => clearInterval(notifPollRef.current);
  }, [fetchNotifications]);

  const handleMarkRead = async () => {
    try {
      await adminApi.patch("/social/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch { toast.error("Failed to mark as read"); }
  };

  const handleClearNotifications = async () => {
    try {
      await adminApi.delete("/social/notifications");
      setNotifications([]);
      setUnreadCount(0);
    } catch { toast.error("Failed to clear"); }
  };

  // ── Form submit ─────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.priceNgn) {
      toast.error("Title, category and price are required"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        priceNgn:  Number(form.priceNgn),
        quantity:  editingId ? 1 : Number(form.quantity) || 1,
      };

      if (editingId) {
        await adminApi.put(`/social/accounts/${editingId}`, payload);
        toast.success("Account updated");
      } else {
        const res = await adminApi.post("/social/accounts", payload);
        toast.success(`${res.data.count || 1} account(s) added`);
      }

      setShowForm(false); setEditingId(null); setForm(EMPTY_FORM);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (account) => {
    setForm({
      title:       account.title || "",
      category:    account.category || "facebook",
      description: account.description || "",
      priceNgn:    account.priceNgn || "",
      previewUrl:  account.previewUrl || "",
      country:     account.country || "",
      quantity:    1,
      credentials: {
        email:    account.credentials?.email    || "",
        password: account.credentials?.password || "",
        extra:    account.credentials?.extra    || "",
      },
    });
    setEditingId(account._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this account? Cannot be undone.")) return;
    try {
      await adminApi.delete(`/social/accounts/${id}`);
      toast.success("Deleted"); fetchAll();
    } catch (err) { toast.error(err.response?.data?.message || "Delete failed"); }
  };

  const handleToggle = async (id) => {
    try {
      const res = await adminApi.patch(`/social/accounts/${id}/toggle`);
      toast.success(res.data.message); fetchAll();
    } catch { toast.error("Toggle failed"); }
  };

  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`Delete the empty category "${group.title}" (${group.category})? This removes its historical records. This cannot be undone.`)) return;
    try {
      const res = await adminApi.delete("/social/groups", {
        data: { title: group.title, category: group.category },
      });
      toast.success(res.data.message);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete category");
    }
  };

  const toggleGroupExpand = (key) => setExpandedGroups((p) => ({ ...p, [key]: !p[key] }));

  const filteredAccounts = accounts.filter((a) => {
    if (!search.trim()) return true;
    return a.title?.toLowerCase().includes(search.toLowerCase()) ||
           a.category?.toLowerCase().includes(search.toLowerCase());
  });

  const filteredGroups = groups.filter((g) => {
    if (!search.trim()) return true;
    return g.title?.toLowerCase().includes(search.toLowerCase()) ||
           g.category?.toLowerCase().includes(search.toLowerCase());
  });

  // Summary counts for alert banner
  const outOfStockCount = groups.filter((g) => g.available === 0).length;
  const lowStockCount   = groups.filter((g) => g.available > 0 && g.available <= LOW_STOCK_THRESHOLD).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Social Logs</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage all social media account listings</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => setPageTab("manual")}
              className={`px-3 py-2 text-xs font-medium transition ${pageTab === "manual" ? "bg-purple-600 text-white" : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300"}`}
            >
              Manual / VaultAcc
            </button>
            <button
              onClick={() => setPageTab("sameeha")}
              className={`px-3 py-2 text-xs font-medium transition ${pageTab === "sameeha" ? "bg-pink-600 text-white" : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300"}`}
            >
              TikTok (Sameeha)
            </button>
            <button
              onClick={() => setPageTab("shopvia")}
              className={`px-3 py-2 text-xs font-medium transition ${pageTab === "shopvia" ? "bg-teal-600 text-white" : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300"}`}
            >
              Logs (Shopvia)
            </button>
          </div>
          <NotificationBell
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkRead={handleMarkRead}
            onClear={handleClearNotifications}
            onRefresh={fetchNotifications}
          />
          <button
            onClick={() => { setShowForm(true); setEditingId(null); setForm(EMPTY_FORM); }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition"
          >
            <Plus size={16} />
            Add Account
          </button>
        </div>
      </div>

      {/* Stock Alert Banner — shown when there are critical items */}
      {(outOfStockCount > 0 || lowStockCount > 0) && (
        <div className={`flex items-start gap-3 rounded-xl p-4 border ${
          outOfStockCount > 0
            ? "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800"
            : "bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800"
        }`}>
          <AlertTriangle size={16} className={outOfStockCount > 0 ? "text-red-500 flex-shrink-0 mt-0.5" : "text-amber-500 flex-shrink-0 mt-0.5"} />
          <div>
            <p className={`text-sm font-semibold ${outOfStockCount > 0 ? "text-red-700 dark:text-red-300" : "text-amber-700 dark:text-amber-300"}`}>
              Stock Alert
            </p>
            <p className={`text-xs mt-0.5 ${outOfStockCount > 0 ? "text-red-600 dark:text-red-400" : "text-amber-600 dark:text-amber-400"}`}>
              {outOfStockCount > 0 && `${outOfStockCount} categor${outOfStockCount === 1 ? "y" : "ies"} out of stock. `}
              {lowStockCount > 0 && `${lowStockCount} categor${lowStockCount === 1 ? "y" : "ies"} running low (≤${LOW_STOCK_THRESHOLD} left).`}
              {" "}Critical items are shown at the top.
            </p>
          </div>
        </div>
      )}

      {pageTab === "sameeha" ? (
        <SameehaOverridesTab />
      ) : pageTab === "shopvia" ? (
        <ShopviaOverridesTab />
      ) : (
      <>
      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-semibold text-gray-900 dark:text-white">
              {editingId ? "Edit Account" : "Add New Account"}
            </h2>
            <button onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
              <X size={18} className="text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                  Title * <span className="text-gray-400 font-normal">(same title + category = same stock group)</span>
                </label>
                <input
                  type="text" value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Spain TikTok 500+ Followers" required
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Category *</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                >
                  {CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Price (₦) *</label>
                <input
                  type="number" value={form.priceNgn} min="1" required
                  onChange={(e) => setForm({ ...form, priceNgn: e.target.value })}
                  placeholder="2500"
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                />
              </div>

              {!editingId && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Quantity to Add
                  </label>
                  <input
                    type="number" min="1" max="1000"
                    value={form.quantity}
                    onChange={(e) => setForm({ ...form, quantity: Math.max(1, parseInt(e.target.value) || 1) })}
                    className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Country</label>
                <input
                  type="text" value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  placeholder="Nigeria, USA, Spain"
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Preview URL</label>
                <input
                  type="url" value={form.previewUrl}
                  onChange={(e) => setForm({ ...form, previewUrl: e.target.value })}
                  placeholder="https://facebook.com/profile"
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Account details visible to users before purchase..."
                  rows={2}
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white resize-none"
                />
              </div>
            </div>

            {/* Credentials */}
            <div className="border border-purple-200 dark:border-purple-800 rounded-xl p-4 bg-purple-50 dark:bg-purple-900/10">
              <p className="text-xs font-semibold text-purple-700 dark:text-purple-400 uppercase tracking-wide mb-3">
                🔐 Login Details (hidden until purchase)
              </p>
              {!editingId && Number(form.quantity) > 1 && (
                <div className="mb-3 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    ⚠️ Adding {form.quantity} accounts with these same credentials. For unique credentials, add one at a time.
                  </p>
                </div>
              )}
              <textarea
                value={form.credentials.email}
                onChange={(e) => setForm({ ...form, credentials: { ...form.credentials, email: e.target.value } })}
                placeholder={`Enter all login details here.\nFormat example:\n61582255154115:poBOVEkWUz:MauricioBrahler63@hotmail.com\nhttps://www.facebook.com/profile.php?id=61582255154115`}
                rows={5}
                className="w-full px-3 py-2.5 text-sm font-mono bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">
                Put everything here — email, password, codes, links. Shown as one copyable line after purchase.
              </p>
            </div>

            <div className="flex gap-3 pt-1">
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }}
                className="flex-1 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition"
              >
                Cancel
              </button>
              <button
                type="submit" disabled={saving}
                className="flex-1 py-2.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg transition flex items-center justify-center gap-2"
              >
                {saving ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Saving...</>
                ) : (
                  <><Save size={15} />{editingId ? "Save Changes" : `Add ${Number(form.quantity) > 1 ? `${form.quantity} Accounts` : "Account"}`}</>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search accounts..."
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
          />
        </div>
        <select
          value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
        >
          <option value="all">All Categories</option>
          {CATEGORIES.map((c) => <option key={c} value={c} className="capitalize">{c}</option>)}
        </select>
        <select
          value={filterSold} onChange={(e) => setFilterSold(e.target.value)}
          className="px-3 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
        >
          <option value="all">All Status</option>
          <option value="false">Available</option>
          <option value="true">Sold</option>
        </select>
        <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <button
            onClick={() => setViewMode("grouped")}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition ${viewMode === "grouped" ? "bg-purple-600 text-white" : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300"}`}
          >
            <Layers size={13} /> Grouped
          </button>
          <button
            onClick={() => setViewMode("all")}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition ${viewMode === "all" ? "bg-purple-600 text-white" : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300"}`}
          >
            <Package size={13} /> All
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="flex gap-3 flex-wrap text-sm text-gray-500 dark:text-gray-400">
        <span>Total: <strong className="text-gray-900 dark:text-white">{pagination.total || 0}</strong></span>
        <span>·</span>
        <span>Available: <strong className="text-green-600">{accounts.filter((a) => !a.isSold && a.isActive).length}</strong></span>
        <span>·</span>
        <span>Sold: <strong className="text-gray-600 dark:text-gray-400">{accounts.filter((a) => a.isSold).length}</strong></span>
        <span>·</span>
        <span>Disabled: <strong className="text-amber-600">{accounts.filter((a) => !a.isSold && !a.isActive).length}</strong></span>
      </div>

      {/* Content */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 bg-white dark:bg-gray-900 rounded-xl animate-pulse border border-gray-200 dark:border-gray-800" />
          ))}
        </div>
      ) : viewMode === "grouped" ? (
        <div className="space-y-2">
          {filteredGroups.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">No accounts found</div>
          ) : (
            filteredGroups.map((group) => (
              <div key={group.groupKey} className={`bg-white dark:bg-gray-900 rounded-xl border overflow-hidden transition ${
                group.available === 0
                  ? "border-red-200 dark:border-red-900"
                  : group.available <= LOW_STOCK_THRESHOLD
                  ? "border-amber-200 dark:border-amber-900"
                  : "border-gray-200 dark:border-gray-800"
              }`}>
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition"
                  onClick={() => toggleGroupExpand(group.groupKey)}
                >
                  <button className="flex-shrink-0">
                    {expandedGroups[group.groupKey]
                      ? <ChevronDown size={16} className="text-purple-500" />
                      : <ChevronRight size={16} className="text-gray-400" />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{group.title}</p>
                      <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full capitalize">
                        {group.category}
                      </span>
                    </div>
                    <p className="text-xs text-purple-600 dark:text-purple-400 font-medium mt-0.5">
                      ₦{group.priceNgn?.toLocaleString()}
                    </p>
                  </div>

                  {/* Stock badge — smart visual indicator */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <StockBadge available={group.available} />
                    {group.sold > 0 && (
                      <span className="text-xs text-gray-400 hidden sm:inline">{group.sold} sold</span>
                    )}
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setForm({
                        ...EMPTY_FORM,
                        title:       group.title,
                        category:    group.category,
                        priceNgn:    group.priceNgn,
                        description: group.description || "",
                      });
                      setEditingId(null);
                      setShowForm(true);
                      window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-xs text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition"
                  >
                    <Plus size={11} /> Add stock
                  </button>

                  {group.available === 0 && group.source === "manual" && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteGroup(group);
                      }}
                      className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-xs text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition"
                    >
                      <Trash2 size={11} /> Delete category
                    </button>
                  )}
                </div>

                {expandedGroups[group.groupKey] && (
                  <div className="border-t border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
                    {group.accounts.map((account) => (
                      <AdminAccountRow
                        key={account._id}
                        account={account}
                        onEdit={() => handleEdit(account)}
                        onDelete={() => handleDelete(account._id)}
                        onToggle={() => handleToggle(account._id)}
                        compact
                      />
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredAccounts.length === 0 ? (
            <div className="text-center py-16 text-gray-500 dark:text-gray-400">No accounts found</div>
          ) : (
            filteredAccounts.map((account) => (
              <AdminAccountRow
                key={account._id}
                account={account}
                onEdit={() => handleEdit(account)}
                onDelete={() => handleDelete(account._id)}
                onToggle={() => handleToggle(account._id)}
              />
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {viewMode === "all" && pagination.pages > 1 && (
        <div className="flex justify-center gap-2 pt-2">
          {[...Array(pagination.pages)].map((_, i) => (
            <button
              key={i} onClick={() => fetchAll(i + 1)}
              className={`w-8 h-8 text-sm rounded-lg transition ${
                pagination.page === i + 1
                  ? "bg-purple-600 text-white"
                  : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-purple-400"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
}

// ── Sameeha Product Overrides Tab ───────────────────────────────────────────
function SameehaOverridesTab() {
  const [products,        setProducts]        = useState([]);
  const [categoryOptions, setCategoryOptions]  = useState([]);
  const [loading,         setLoading]          = useState(true);
  const [editing,         setEditing]          = useState(null);
  const [draft,           setDraft]            = useState({});
  const [saving,          setSaving]           = useState(false);
  const [categoryFilter,  setCategoryFilter]   = useState("all");
  const [visibilityFilter,setVisibilityFilter] = useState("all"); // all | visible | hidden

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/social/sameeha-products");
      if (res.data.success) {
        setProducts(res.data.products || []);
        setCategoryOptions(res.data.existingDisplayCategories || []);
      }
    } catch {
      toast.error("Failed to load Sameeha products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const startEdit = (p) => {
    setEditing(p.sameehaId);
    setDraft({
      title: p.title,
      description: p.description,
      previewUrl: p.previewUrl || "",
      isEnabled: p.isEnabled,
      displayCategory: p.displayCategory,
      priceOverrideNgn: p.priceOverrideNgn || "",
      marginOverridePercent: p.marginOverridePercent ?? "",
    });
  };

  const saveEdit = async (p) => {
    setSaving(true);
    try {
      await adminApi.put("/social/overrides", {
        source: "sameeha",
        externalId: p.sameehaId,
        ...draft,
      });
      toast.success("Saved — live on the marketplace now");
      setEditing(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const filteredProducts = products.filter((p) => {
    if (categoryFilter !== "all" && !p.sameehaCategory.toLowerCase().includes(categoryFilter.toLowerCase())) return false;
    if (visibilityFilter === "visible" && !p.isEnabled) return false;
    if (visibilityFilter === "hidden" && p.isEnabled) return false;
    return true;
  });

  const sameehaCategories = [...new Set(products.map((p) => p.sameehaCategory))].sort();
  const visibleCount = products.filter(p => p.isEnabled).length;

  if (loading) {
    return <div className="text-center py-16 text-gray-500 dark:text-gray-400">Loading Sameeha catalog...</div>;
  }

  return (
    <div className="space-y-3">
      <div className="bg-pink-50 dark:bg-pink-900/10 border border-pink-200 dark:border-pink-800 rounded-xl p-3">
        <p className="text-xs text-pink-700 dark:text-pink-400">
          A product only appears on the site once you toggle <strong>"Visible on site"</strong> ON and save —
          setting the category alone does not make it visible. Type any category name you like (VPN, Old Gmail, etc.) —
          existing ones are suggested as you type. Each product can have its own price: either a fixed ₦ amount, or its own
          margin % (independent of the global Sameeha margin in Settings). Currently <strong>{visibleCount}</strong> of {products.length} products are visible.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:text-white"
        >
          <option value="all">All Sameeha categories ({products.length})</option>
          {sameehaCategories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <select
          value={visibilityFilter}
          onChange={(e) => setVisibilityFilter(e.target.value)}
          className="px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:text-white"
        >
          <option value="all">All statuses</option>
          <option value="visible">Visible only</option>
          <option value="hidden">Hidden only</option>
        </select>
      </div>

      <datalist id="sameeha-category-options">
        {categoryOptions.map((c) => <option key={c} value={c} />)}
      </datalist>

      {filteredProducts.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">No products found</div>
      ) : (
        filteredProducts.map((p) => (
          <div key={p.sameehaId} className={`bg-white dark:bg-gray-900 rounded-xl border p-4 ${p.isEnabled ? "border-green-200 dark:border-green-800" : "border-gray-200 dark:border-gray-800"}`}>
            {editing === p.sameehaId ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <button type="button" onClick={() => setDraft({ ...draft, isEnabled: !draft.isEnabled })} className="flex items-center gap-2">
                    {draft.isEnabled
                      ? <ToggleRight size={26} className="text-green-600" />
                      : <ToggleLeft size={26} className="text-gray-400" />}
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                      {draft.isEnabled ? "Visible on site" : "Hidden from site"}
                    </span>
                  </button>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Site Category * — type anything (VPN, Old Gmail, Facebook...)
                  </label>
                  <input
                    type="text" list="sameeha-category-options"
                    value={draft.displayCategory}
                    onChange={(e) => setDraft({ ...draft, displayCategory: e.target.value })}
                    placeholder="e.g. vpn, old gmail, tiktok"
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Title (leave blank to use "{p.originalName}")</label>
                  <input
                    type="text" value={draft.title}
                    onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description</label>
                  <textarea
                    value={draft.description} rows={2}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:text-white resize-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">View URL (optional)</label>
                  <input
                    type="url" value={draft.previewUrl}
                    onChange={(e) => setDraft({ ...draft, previewUrl: e.target.value })}
                    placeholder="https://..."
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:text-white"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Fixed Price (₦)
                    </label>
                    <input
                      type="number" min="0" value={draft.priceOverrideNgn}
                      onChange={(e) => setDraft({ ...draft, priceOverrideNgn: e.target.value, marginOverridePercent: e.target.value ? "" : draft.marginOverridePercent })}
                      placeholder="leave blank"
                      className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      This Product's Margin %
                    </label>
                    <input
                      type="number" min="0" max="500" value={draft.marginOverridePercent}
                      onChange={(e) => setDraft({ ...draft, marginOverridePercent: e.target.value, priceOverrideNgn: e.target.value ? "" : draft.priceOverrideNgn })}
                      placeholder="uses global margin"
                      disabled={!!draft.priceOverrideNgn}
                      className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 dark:text-white disabled:opacity-50"
                    />
                  </div>
                  <p className="col-span-2 text-xs text-gray-400 dark:text-gray-500">
                    Base price ₦{p.basePriceNgn.toLocaleString()} · global margin gives ₦{p.globalMarginPrice.toLocaleString()}.
                    Only one of the two fields above applies — fixed price wins if both are set.
                  </p>
                </div>

                <div className="flex gap-2">
                  <button onClick={() => setEditing(null)} className="flex-1 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg">
                    Cancel
                  </button>
                  <button onClick={() => saveEdit(p)} disabled={saving} className="flex-1 py-2 text-sm font-medium bg-pink-600 hover:bg-pink-700 disabled:bg-pink-400 text-white rounded-lg">
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{p.title}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.isEnabled ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400" : "bg-gray-100 dark:bg-gray-800 text-gray-500"}`}>
                      {p.isEnabled ? "Visible" : "Hidden"}
                    </span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 capitalize">
                      {p.displayCategory}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400">{p.sameehaCategory} · Stock: {p.stock}</p>
                  <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                    ₦{p.priceNgn.toLocaleString()} {
                      p.pricingMode === "fixed" ? "(fixed)" :
                      p.pricingMode === "percent" ? `(${p.marginOverridePercent}% margin)` :
                      "(global margin)"
                    }
                  </p>
                </div>
                <button onClick={() => startEdit(p)} className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-pink-600 dark:text-pink-400 border border-pink-200 dark:border-pink-800 rounded-lg hover:bg-pink-50 dark:hover:bg-pink-900/20 transition">
                  Edit
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ── Shopvia Product Overrides Tab ────────────────────────────────────────────
// Simpler than Sameeha's tab on purpose: Shopvia has no per-product visibility
// toggle or pricing (priced per-category in Settings, everything in stock
// shows automatically) — this is purely for cleaning up title/description text.
const SHOPVIA_ADMIN_CATEGORIES = [
  "facebook", "instagram", "tiktok", "twitter", "pinterest",
  "hotmail", "outlook", "email", "tools", "other",
];

function ShopviaOverridesTab() {
  const [products,       setProducts]       = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [editing,        setEditing]        = useState(null);
  const [draft,          setDraft]          = useState({});
  const [saving,         setSaving]         = useState(false);
  const [search,         setSearch]         = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [editedOnly,     setEditedOnly]     = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/social/shopvia-products");
      if (res.data.success) setProducts(res.data.products || []);
    } catch {
      toast.error("Failed to load Shopvia products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  const startEdit = (p) => {
    setEditing(p.shopviaId);
    setDraft({
      title: p.title,
      description: p.description,
      previewUrl: p.previewUrl || "",
      isEnabled: p.isEnabled,
      priceOverrideNgn: p.priceOverrideNgn || "",
      marginOverridePercent: p.marginOverridePercent ?? "",
    });
  };

  const saveEdit = async (p) => {
    setSaving(true);
    try {
      await adminApi.put("/social/overrides", {
        source: "shopvia",
        externalId: p.shopviaId,
        ...draft,
      });
      toast.success("Saved — live on the marketplace now");
      setEditing(null);
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const resetOverride = async (p) => {
    if (!window.confirm(`Reset "${p.title}" back to its original Shopvia title/description?`)) return;
    setSaving(true);
    try {
      await adminApi.put("/social/overrides", {
        source: "shopvia",
        externalId: p.shopviaId,
        title: "",
        description: "",
        previewUrl: "",
        priceOverrideNgn: "",
        marginOverridePercent: "",
        isEnabled: true,
      });
      toast.success("Reset to original");
      fetchProducts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  const filtered = products.filter((p) => {
    if (categoryFilter !== "all" && p.category !== categoryFilter) return false;
    if (editedOnly && !p.hasOverride) return false;
    if (search.trim()) {
      const term = search.toLowerCase();
      if (!p.title.toLowerCase().includes(term) && !p.description.toLowerCase().includes(term)) return false;
    }
    return true;
  });

  // Group by category for browsing
  const grouped = SHOPVIA_ADMIN_CATEGORIES
    .map((cat) => ({ cat, items: filtered.filter((p) => p.category === cat) }))
    .filter((g) => g.items.length > 0);

  const editedCount = products.filter((p) => p.hasOverride).length;

  if (loading) {
    return <div className="text-center py-16 text-gray-500 dark:text-gray-400">Loading Shopvia catalog...</div>;
  }

  return (
    <div className="space-y-3">
      <div className="bg-teal-50 dark:bg-teal-900/10 border border-teal-200 dark:border-teal-800 rounded-xl p-3">
        <p className="text-xs text-teal-700 dark:text-teal-400">
          Every in-stock item here already shows on the marketplace automatically — there's no visibility toggle to worry about.
          Edit a title or description below to override the raw text Shopvia sends (often messy or in broken English);
          leave it as-is if it already reads fine. Currently <strong>{editedCount}</strong> of {products.length} products have a custom title/description.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search title or description..."
            className="w-full pl-8 pr-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white capitalize"
        >
          <option value="all">All categories ({products.length})</option>
          {SHOPVIA_ADMIN_CATEGORIES.map((c) => (
            <option key={c} value={c} className="capitalize">
              {c} ({products.filter((p) => p.category === c).length})
            </option>
          ))}
        </select>
        <button
          onClick={() => setEditedOnly((v) => !v)}
          className={`px-3 py-2 text-xs font-medium rounded-lg border transition ${
            editedOnly
              ? "bg-teal-600 text-white border-teal-600"
              : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700"
          }`}
        >
          Edited only
        </button>
      </div>

      {grouped.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">No products found</div>
      ) : (
        grouped.map(({ cat, items }) => (
          <div key={cat} className="space-y-2">
            <div className="flex items-center gap-2 pt-2">
              <span className="w-1 h-4 bg-teal-500 rounded-full" />
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white capitalize">{cat}</h3>
              <span className="text-xs text-gray-400">({items.length})</span>
            </div>

            {items.map((p) => (
              <div key={p.shopviaId} className={`bg-white dark:bg-gray-900 rounded-xl border p-4 ${p.hasOverride ? "border-teal-200 dark:border-teal-800" : "border-gray-200 dark:border-gray-800"}`}>
                {editing === p.shopviaId ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <button type="button" onClick={() => setDraft({ ...draft, isEnabled: !draft.isEnabled })} className="flex items-center gap-2">
                        {draft.isEnabled
                          ? <ToggleRight size={26} className="text-green-600" />
                          : <ToggleLeft size={26} className="text-gray-400" />}
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                          {draft.isEnabled ? "Visible on site" : "Hidden from site"}
                        </span>
                      </button>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Title (original: "{p.originalTitle}")
                      </label>
                      <input
                        type="text" value={draft.title}
                        onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description</label>
                      <textarea
                        value={draft.description} rows={3}
                        onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                        className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white resize-none"
                      />
                      <p className="text-xs text-gray-400 mt-1">Original: "{p.originalDescription}"</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">View URL (optional)</label>
                      <input
                        type="url" value={draft.previewUrl}
                        onChange={(e) => setDraft({ ...draft, previewUrl: e.target.value })}
                        placeholder="https://..."
                        className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Fixed Price (₦)</label>
                        <input
                          type="number" min="0" value={draft.priceOverrideNgn}
                          onChange={(e) => setDraft({ ...draft, priceOverrideNgn: e.target.value, marginOverridePercent: e.target.value ? "" : draft.marginOverridePercent })}
                          placeholder="leave blank"
                          className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">This Product's Margin %</label>
                        <input
                          type="number" min="0" max="500" value={draft.marginOverridePercent}
                          onChange={(e) => setDraft({ ...draft, marginOverridePercent: e.target.value, priceOverrideNgn: e.target.value ? "" : draft.priceOverrideNgn })}
                          placeholder="uses category margin"
                          disabled={!!draft.priceOverrideNgn}
                          className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 dark:text-white disabled:opacity-50"
                        />
                      </div>
                      <p className="col-span-2 text-xs text-gray-400 dark:text-gray-500">
                        Base ₦{p.basePriceNgn.toLocaleString()} (USD × live rate) · category margin gives ₦{p.globalMarginPrice.toLocaleString()}.
                        Fixed price wins if both are set.
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setEditing(null)} className="flex-1 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg">
                        Cancel
                      </button>
                      <button onClick={() => saveEdit(p)} disabled={saving} className="flex-1 py-2 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg">
                        {saving ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{p.title}</p>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.isEnabled ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400" : "bg-gray-100 dark:bg-gray-800 text-gray-500"}`}>
                          {p.isEnabled ? "Visible" : "Hidden"}
                        </span>
                        {p.hasOverride && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex-shrink-0">
                            Edited
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{p.description}</p>
                      <p className="text-xs text-gray-400 mt-0.5">Stock: {p.stock} · ${p.priceUsd}</p>
                      <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                        ₦{p.priceNgn.toLocaleString()} {
                          p.pricingMode === "fixed" ? "(fixed)" :
                          p.pricingMode === "percent" ? `(${p.marginOverridePercent}% margin)` :
                          "(category margin)"
                        }
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {p.hasOverride && (
                        <button onClick={() => resetOverride(p)} className="px-2 py-1.5 text-xs text-gray-400 hover:text-red-500 transition">
                          Reset
                        </button>
                      )}
                      <button onClick={() => startEdit(p)} className="px-3 py-1.5 text-xs font-medium text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition">
                        Edit
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
}

function AdminAccountRow({ account, onEdit, onDelete, onToggle, compact }) {
  return (
    <div className={`flex items-center gap-3 transition ${compact ? "px-6 py-2.5" : "px-4 py-3"} ${
      account.isSold ? "opacity-50" : account.isActive ? "hover:bg-gray-50 dark:hover:bg-gray-800/50" : "bg-orange-50/50 dark:bg-orange-900/10"
    }`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {!compact && <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{account.title}</p>}
          {account.country && <span className="text-xs text-gray-400">🌍 {account.country}</span>}
          {account.isSold && <span className="text-xs px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 rounded-full">Sold</span>}
          {!account.isActive && !account.isSold && <span className="text-xs px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 rounded-full">Disabled</span>}
        </div>
        {!compact && <p className="text-xs text-purple-600 dark:text-purple-400 font-medium mt-0.5">₦{account.priceNgn?.toLocaleString()}</p>}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {account.previewUrl && (
          <a href={account.previewUrl} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition">
            <Eye size={14} className="text-gray-400" />
          </a>
        )}
        {!account.isSold && (
          <>
            <button onClick={onToggle} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition">
              {account.isActive ? <ToggleRight size={14} className="text-green-500" /> : <ToggleLeft size={14} className="text-gray-400" />}
            </button>
            <button onClick={onEdit} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition">
              <Pencil size={14} className="text-gray-400 hover:text-blue-500" />
            </button>
            <button onClick={onDelete} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition">
              <Trash2 size={14} className="text-gray-400 hover:text-red-500" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}
